import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { Toast } from '../components/ui/Notification';
import { 
  FiUsers, FiInbox, FiClock, FiAlertTriangle, FiPlus, FiArrowRight, 
  FiRefreshCcw, FiActivity, FiLoader, FiShield, FiSettings
} from 'react-icons/fi';

export default function AdminDashboard() {
  const { adminUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [error, setError] = useState(null);

  const normalizeDashboardData = useCallback((rawStats, clients = []) => {
    const normalized = {
      activeClients: rawStats?.active_clients || 0,
      leadsToday: rawStats?.leads_today || 0,
      expiringSoon: rawStats?.expiring_soon || 0,
      failedAlerts: rawStats?.errors_total || 0
    };

    if (clients.length > 0 && normalized.activeClients === 0) {
      normalized.activeClients = clients.filter(c => c.status === 'Active').length;
      normalized.expiringSoon = clients.filter(c => c.days_remaining !== undefined && c.days_remaining >= 0 && c.days_remaining <= 7).length;
    }
    return normalized;
  }, []);

  const fetchDashboardData = async (isManual = false) => {
    if (isManual) {
      setRefreshing(true);
      setNotification({ message: 'Refreshing system intelligence...', type: 'info' });
    } else {
      setLoading(true);
    }
    
    setError(null);
    try {
      const token = adminUser.admin_session_token;
      const [statsRes, clientsRes] = await Promise.all([
        adminApi.getStats(token).catch(() => ({ success: false })),
        adminApi.getClients(token).catch(() => ({ success: false }))
      ]);

      if (statsRes.success || clientsRes.success) {
        setStats(normalizeDashboardData(statsRes.stats || {}, clientsRes.clients || []));
        if (isManual) setNotification({ message: 'System intelligence synchronized.', type: 'success' });
      } else {
        setError("Failed to synchronize dashboard.");
      }
    } catch (e) {
      setError("Gateway connection failed.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { if (adminUser) fetchDashboardData(); }, [adminUser]);

  const cards = [
    { label: 'Active Clients', val: stats?.activeClients || 0, icon: FiUsers, gradient: 'from-primary to-accent' },
    { label: 'Leads Captured', val: stats?.leadsToday || 0, icon: FiInbox, gradient: 'from-blue-500 to-primary' },
    { label: 'Attention Needed', val: stats?.expiringSoon || 0, icon: FiClock, gradient: 'from-warning to-amber-500' },
    { label: 'System Errors', val: stats?.failedAlerts || 0, icon: FiAlertTriangle, gradient: 'from-error to-red-500' },
  ];

  if (loading) return (
    <div className="animate-pulse space-y-8">
      <div className="h-10 w-64 bg-slate-200 rounded-xl"></div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-36 bg-surface rounded-xl"></div>)}
      </div>
      <div className="h-80 bg-surface rounded-xl"></div>
    </div>
  );

  return (
    <div className="space-y-8">
      {notification && <Toast {...notification} onClose={() => setNotification(null)} />}
      
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-ink tracking-tight mb-1">Dashboard</h1>
          <div className="flex items-center gap-4">
            <p className="text-sm text-muted">System Overview</p>
            <button onClick={() => fetchDashboardData(true)} disabled={refreshing} className="flex items-center gap-2 text-sm text-primary hover:text-accent transition-all disabled:opacity-50">
              <SafeIcon icon={refreshing ? FiLoader : FiRefreshCcw} className={refreshing ? 'animate-spin' : ''} />
              <span>{refreshing ? 'Syncing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
        <button onClick={() => navigate('/admin/clients/new')} className="px-6 py-3 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-medium flex items-center gap-2 shadow-card hover:-translate-y-0.5 transition-premium active:scale-95">
          <SafeIcon icon={FiPlus} />
          <span>New Client</span>
        </button>
      </header>

      {error && (
        <div className="bg-error/5 border border-error/20 rounded-xl p-4 flex items-center gap-3 text-error">
          <SafeIcon icon={FiAlertTriangle} />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map(card => (
          <div key={card.label} className="bg-surface p-6 rounded-xl shadow-card border border-border relative overflow-hidden group hover:shadow-premium transition-premium">
            <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-tr ${card.gradient} opacity-5 rounded-full -mr-8 -mt-8 transition-transform group-hover:scale-125`}></div>
            <div className={`w-10 h-10 bg-gradient-to-tr ${card.gradient} text-white rounded-lg flex items-center justify-center relative z-10 shadow-soft mb-4`}>
              <SafeIcon icon={card.icon} />
            </div>
            <p className="text-sm text-muted mb-1">{card.label}</p>
            <p className="text-2xl font-bold text-ink tracking-tight">{card.val}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface rounded-xl p-6 border border-border shadow-card">
          <h3 className="text-lg font-semibold text-ink mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'Manage Clients', to: '/admin/clients', desc: 'Configure partner access', icon: FiUsers },
              { label: 'View Leads', to: '/admin/leads', desc: 'Browse all submissions', icon: FiInbox },
              { label: 'System Health', to: '/admin/logs', desc: 'Monitor events', icon: FiActivity },
              { label: 'Configure', to: '/admin/settings', desc: 'Gateway settings', icon: FiSettings },
            ].map(action => (
              <button key={action.label} onClick={() => navigate(action.to)} className="flex flex-col items-start p-4 bg-slate-50 hover:bg-surface hover:shadow-card rounded-lg transition-premium border border-border" >
                <div className="w-8 h-8 bg-white group-hover:bg-primary group-hover:text-white rounded-md flex items-center justify-center text-primary shadow-sm mb-2 transition-colors">
                  <SafeIcon icon={action.icon} />
                </div>
                <p className="font-medium text-ink text-sm">{action.label}</p>
                <p className="text-xs text-muted">{action.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-slate-900 rounded-xl p-6 text-white relative overflow-hidden shadow-card">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-accent">
                <SafeIcon icon={FiShield} />
              </div>
              <h3 className="text-lg font-semibold">System Health</h3>
            </div>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-sm text-slate-400">Gateway Status</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-success">Connected</span>
                </div>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-sm text-slate-400">Active Errors</span>
                <span className={`text-lg font-semibold ${stats?.failedAlerts > 0 ? 'text-error' : 'text-white'}`}>
                  {stats?.failedAlerts || 0}
                </span>
              </div>
            </div>
            <button onClick={() => navigate('/admin/settings')} className="w-full flex items-center justify-center gap-2 text-sm font-medium text-accent hover:text-white transition-all group" >
              <span>Diagnostics</span>
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}